import ApplicationInvoiceSection from "./ApplicationInvoiceSection";
import ContestDatesSection from "../shared/ContestDatesSection";
import ContestRegistrationSection from "../shared/ContestRegistrationSection";
import StatusChip from "../shared/StatusChip";
import { ApplicationWithId } from "../../interfaces/IApplications";
import { AppUser } from "../../interfaces/IUsers";
import {
    Assignment,
    AttachMoney,
    Business,
    Group,
    Slideshow
    } from "@mui/icons-material";
import {
    Box,
    Grid,
    Tooltip,
    Typography
    } from "@mui/material";
import { ContestWithId } from "../../interfaces/IContests";

interface ApplicationInfoSectionProps {
  application: ApplicationWithId;
  contest?: ContestWithId;
  user?: AppUser;
  participantsCount: number;
  presentationsCount: number;
  onApplicationUpdate?: (applicationId: string, data: Partial<ApplicationWithId>) => Promise<{ success: boolean; error?: string }>;
}

export default function ApplicationInfoSection({
  application,
  contest,
  user,
  participantsCount,
  presentationsCount,
  onApplicationUpdate,
}: ApplicationInfoSectionProps) {
  return (
    <Box sx={{ p: 2 }}>
      {/* Nagłówek z ikoną i statusem */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Assignment sx={{ color: "#6366f1", fontSize: 20 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
          Informacje o zgłoszeniu
        </Typography>
        <StatusChip type="application" value={application.status} size="small" />
      </Box>

      {/* Pierwszy wiersz - podstawowe informacje */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Business sx={{ fontSize: 16, color: "#6c6c6c" }} />
            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#6c6c6c" }}>
              Organizacja:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {application.userOrganizationName || user?.organizationName || "Brak"}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <ContestDatesSection contest={contest} label="Termin:" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ContestRegistrationSection contest={contest} />
        </Grid>
      </Grid>

      {/* Drugi wiersz - statystyki z ikonami i tooltipami */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Group sx={{ fontSize: 16, color: "#6c6c6c" }} />
            <Tooltip title="Ilość zgłoszonych zawodników" arrow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#6c6c6c" }}>
                  Zawodnicy:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#6366f1" }}>
                  {participantsCount}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Slideshow sx={{ fontSize: 16, color: "#6c6c6c" }} />
            <Tooltip title="Ilość dodanych prezentacji" arrow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#6c6c6c" }}>
                  Prezentacje:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#6366f1" }}>
                  {presentationsCount}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AttachMoney sx={{ fontSize: 16, color: "#6c6c6c" }} />
            <Tooltip title="Całkowita kwota zgłoszenia" arrow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#6c6c6c" }}>
                  Kwota:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#6366f1" }}>
                  {application.totalCost} PLN
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      {/* Sekcja faktury */}
      {onApplicationUpdate && (
        <ApplicationInvoiceSection
          application={application}
          user={user || null}
          onUpdate={onApplicationUpdate}
        />
      )}
    </Box>
  );
}
